import { Ecommerce } from "./nexus-app";
import { formatInTimeZone, toZonedTime } from "date-fns-tz";

export type WeekdayKey =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

export type OpeningHoursDay = {
  day: WeekdayKey;
  from?: string;
  to?: string;
  closed: boolean;
};

const WEEK_DAYS_ORDER: WeekdayKey[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const DEFAULT_TIMEZONE = "Europe/Warsaw";

function getStoreTimeZone(): string {
  return DEFAULT_TIMEZONE;
}

function getWeekdayKeyInTz(date: Date, timeZone: string): WeekdayKey {
  const zoned = toZonedTime(date, timeZone);
  const day = zoned.getDay();
  const map: Record<number, WeekdayKey> = {
    0: "sunday",
    1: "monday",
    2: "tuesday",
    3: "wednesday",
    4: "thursday",
    5: "friday",
    6: "saturday",
  };
  return map[day];
}

function minutesFromHHmm(time: string): number {
  const [h, m] = time.split(":").map((x) => Number(x));
  return h * 60 + m;
}

function utcHHmmToZonedHHmm(params: {
  utcHHmm: string;
  now: Date;
  timeZone: string;
}): string {
  const { utcHHmm, now, timeZone } = params;

  const yyyyMmDd = formatInTimeZone(now, timeZone, "yyyy-MM-dd");
  const [y, mo, d] = yyyyMmDd.split("-").map(Number);

  const [h, m] = utcHHmm.split(":").map(Number);

  const utcDate = new Date(Date.UTC(y, mo - 1, d, h, m, 0, 0));

  return formatInTimeZone(utcDate, timeZone, "HH:mm");
}

function nowMinutesInTz(now: Date, timeZone: string): number {
  const zoned = toZonedTime(now, timeZone);
  return zoned.getHours() * 60 + zoned.getMinutes();
}

export type StoreSchedule = Pick<
  Ecommerce,
  "openingHours" | "closedDays" | "posOfflineSince"
>;

export type StoreStatus =
  | { isOpen: true; reason: "open"; today: OpeningHoursDay | null }
  | {
      isOpen: false;
      reason:
        | "closed-day"
        | "closed-date"
        | "outside-hours"
        | "no-hours"
        | "pos-offline";
      today: OpeningHoursDay | null;
      message: string;
    };

function formatHoursLine(params: {
  key: WeekdayKey;
  day: OpeningHoursDay;
  now: Date;
  timeZone: string;
}): string {
  const { day, now, timeZone } = params;

  // `day.day` bywa nadpisywane spolszczoną etykietą przez panel ecommerce-settings
  // (np. "Poniedziałek") — do klucza tłumaczenia zawsze bierzemy kanoniczny `key`.
  const label = params.key;

  if (day.closed) return `${label}: closed`;
  if (!day.from || !day.to) return `${label}: no-data`;

  const fromLocal = utcHHmmToZonedHHmm({ utcHHmm: day.from, now, timeZone });
  const toLocal = utcHHmmToZonedHHmm({ utcHHmm: day.to, now, timeZone });

  return `${label}: ${fromLocal}–${toLocal}`;
}

export function buildWeeklyOpeningHours(
  ecommerce: StoreSchedule,
  now = new Date()
): string[] {
  const openingHours = ecommerce.openingHours as
    | Partial<Record<WeekdayKey, OpeningHoursDay>>
    | undefined;

  if (!openingHours) return [];

  const timeZone = getStoreTimeZone();

  return WEEK_DAYS_ORDER.map((key) => {
    const day = openingHours[key];
    if (!day) return null;
    return formatHoursLine({ key, day, now, timeZone });
  }).filter(Boolean) as string[];
}

function buildWeeklyMessage(ecommerce: StoreSchedule, now: Date): string {
  const lines = buildWeeklyOpeningHours(ecommerce, now);

  if (lines.length === 0) {
    return "Brak danych o godzinach otwarcia.";
  }

  return `Godziny otwarcia w tygodniu:\n${lines.join("\n")}`;
}

export function getStoreStatus(
  ecommerce: StoreSchedule,
  now = new Date()
): StoreStatus {
  const timeZone = getStoreTimeZone();

  const todayKey = getWeekdayKeyInTz(now, timeZone);
  const today =
    (ecommerce.openingHours?.[todayKey] as OpeningHoursDay | undefined) ?? null;

  const closedDays = ecommerce.closedDays ?? [];
  const todayDate = formatInTimeZone(now, timeZone, "yyyy-MM-dd");

  const message = buildWeeklyMessage(ecommerce, now);

  if (closedDays.includes(todayDate)) {
    return { isOpen: false, reason: "closed-date", today, message };
  }

  if (!today) {
    return { isOpen: false, reason: "no-hours", today: null, message };
  }

  if (today.closed) {
    return { isOpen: false, reason: "closed-day", today, message };
  }

  if (!today.from || !today.to) {
    return { isOpen: false, reason: "no-hours", today, message };
  }

  const fromLocal = utcHHmmToZonedHHmm({ utcHHmm: today.from, now, timeZone });
  const toLocal = utcHHmmToZonedHHmm({ utcHHmm: today.to, now, timeZone });

  const fromMin = minutesFromHHmm(fromLocal);
  const toMin = minutesFromHHmm(toLocal);
  const nowMin = nowMinutesInTz(now, timeZone);

  const isOpen =
    toMin >= fromMin
      ? nowMin >= fromMin && nowMin < toMin
      : nowMin >= fromMin || nowMin < toMin;

  if (isOpen && ecommerce.posOfflineSince) {
    return { isOpen: false, reason: "pos-offline", today, message };
  }

  if (isOpen) {
    return { isOpen: true, reason: "open", today };
  }

  return { isOpen: false, reason: "outside-hours", today, message };
}
